<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { X } from 'lucide';
import { computed, inject } from 'vue';
import { SphIcon } from '@/components/base/icon';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<Tag>(), {
    class: '',
    value: null,
    deletable: false,
    interactive: false,
});

const emit = defineEmits<{
    click: []
    delete: []
}>();

const { i18n } = useI18n();

export interface Tag {
    class?: HTMLAttributes['class']
    value?: any
    label: string
    deletable?: boolean
    interactive?: boolean
}

const inTagGroup = inject('sphTagGroup', false);
const interactive = computed(() => props.interactive || props.deletable || inTagGroup);
</script>

<template>
    <div
        class="tag"
        :class="[
            {
                deletable: props.deletable,
                interactive,
            },
            props.class,
        ]"
        @click="() => {
            if (!interactive) {
                return
            }

            emit('click')

            if (props.deletable) {
                emit('delete')
            }
        }"
    >
        <slot>{{ props.label }}</slot>

        <div
            v-if="props.deletable"
            class="delete-button"
            :aria-label="i18n.t('Tag löschen')"
        >
            <SphIcon
                :icon="X"
                size="xs"
            />
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

.tag {
    @apply relative inline-block rounded-md border px-2 py-1 text-xs font-medium leading-tight select-none transition-all duration-200;

    --tag-bg: var(--color-input);
    --tag-border: var(--color-input-dark);
    --tag-text: var(--color-input-foreground);
    --tag-bg-hover: color-mix(in srgb, var(--color-input-dark) 75%, transparent);
    --tag-border-hover: color-mix(in srgb, var(--color-primary) 50%, transparent);
    --tag-text-hover: var(--color-primary);

    background-color: var(--tag-bg);
    border-color: var(--tag-border);
    color: var(--tag-text);

    &.interactive {
        @apply cursor-pointer;

        &:hover {
            background-color: var(--tag-bg-hover);
            border-color: var(--tag-border-hover);
            color: var(--tag-text-hover);

            .delete-button {
                color: var(--tag-text-hover);
            }
        }
    }

    &.deletable {
        @apply pr-6.5;
    }

    .delete-button {
        @apply absolute right-0 top-0 flex items-center justify-center w-7 h-full text-muted-foreground;
    }
}
</style>
